import { useMemo } from "react";

import {
    LendingVaultExpanded,
    serializeStrategyDuration,
    useLendVaults,
    useStrategyDurations,
    useTokenListMetadataByMints
} from "@bridgesplit/abf-react";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { useLendingVaultPagination } from "app/api";
import { DISABLED_APY, percentDecimalsToUi } from "@bridgesplit/utils";

import { CollateralAmountsForm, CollateralFormTableInfo, RateAmountForm, RateFormTableInfo } from "./type";

export function useLendVaultFromParams(vaultAddress: string | undefined) {
    const { params } = useLendingVaultPagination();
    const {
        data: vaults,
        isLoading,
        isFetching
    } = useLendVaults({
        pagination: params,
        filter: { vaultAddresses: vaultAddress ? [vaultAddress] : undefined },
        skip: !vaultAddress
    });

    const vaultInfo = vaults?.find((v) => v.vault.address === vaultAddress);

    if (!vaultInfo) {
        return {
            data: undefined,
            isLoading,
            isFetching,
            notFound: !isLoading && !isFetching && !!vaultAddress
        };
    }

    return {
        data: vaultInfo,
        isLoading,
        isFetching,
        notFound: false
    };
}

export function isCollateralMarketSupported(vaultExpanded: LendingVaultExpanded | undefined, mint: string | undefined) {
    if (!vaultExpanded?.ltvInfos || !mint || !vaultExpanded.ltvInfos[mint]?.durationToLtvInfo) {
        return false;
    }

    return Object.values(vaultExpanded.ltvInfos[mint].durationToLtvInfo).some(
        (ltvInfo) => ltvInfo?.marketInfo !== undefined
    );
}

export function isApyDisabled(
    vaultExpanded: LendingVaultExpanded | undefined,
    mint: string | undefined,
    durations: StrategyDuration[] | undefined
): boolean {
    if (!vaultExpanded?.ltvInfos || !mint || !vaultExpanded.ltvInfos[mint]?.durationToLtvInfo || !durations) {
        return false;
    }

    const ltvInfos = vaultExpanded.ltvInfos[mint].durationToLtvInfo;

    return durations.every((duration) => {
        const serializedDuration = serializeStrategyDuration(duration);
        const ltvInfo = ltvInfos[serializedDuration];
        return ltvInfo?.apy?.toString() === DISABLED_APY;
    });
}

//Transformers
export function convertVaultExpandedToMintToInfo(
    vaultExpanded: LendingVaultExpanded | undefined
): Record<string, CollateralAmountsForm> {
    if (!vaultExpanded?.ltvInfos) {
        return {};
    }

    const tokenMap: Record<string, CollateralAmountsForm> = {};

    for (const [mint, info] of Object.entries(vaultExpanded.ltvInfos)) {
        // Get first duration's LTV info since we only need base values and all are the markets are the same per collateral
        const firstDuration = Object.values(info.durationToLtvInfo)[0];

        if (firstDuration) {
            tokenMap[mint] = {
                ltvUiAmount: percentDecimalsToUi(firstDuration.marketInfo?.ltv),
                liquidationLtvAmount: percentDecimalsToUi(firstDuration.marketInfo?.liquidationThreshold)
            };
        }
    }

    return tokenMap;
}

export function convertVaultExpandedToMintToRateInfo(
    vaultExpanded: LendingVaultExpanded | undefined,
    strategyDurations: StrategyDuration[] | undefined
): Record<string, Record<string, RateAmountForm>> {
    const tokenMap: Record<string, Record<string, RateAmountForm>> = {};

    for (const term of Object.entries(vaultExpanded?.vaultStrategy.terms.assetTerms ?? {})) {
        const [mint, termInfo] = term;
        tokenMap[mint] = {};

        for (const [strategyDuration, apy] of termInfo.durationAndApys) {
            const duration = serializeStrategyDuration(strategyDuration);
            const ltvInfo = vaultExpanded?.ltvInfos?.[mint]?.durationToLtvInfo[duration];
            if (!ltvInfo?.marketInfo) continue;

            tokenMap[mint][duration] = {
                apyAmountUi:
                    apy.toString() === DISABLED_APY || apy === undefined
                        ? undefined
                        : percentDecimalsToUi(Number(apy.toString())).toString()
            };
        }
    }

    for (const mint of Object.keys(vaultExpanded?.ltvInfos ?? {})) {
        if (!tokenMap[mint]) {
            tokenMap[mint] = {};
            // For mints not in tokenMap, set all strategy durations to DISABLED_APY
            strategyDurations?.forEach((duration) => {
                const serializedDuration = serializeStrategyDuration(duration);
                tokenMap[mint][serializedDuration] = {
                    apyAmountUi: undefined
                };
            });
        } else {
            strategyDurations?.forEach((duration) => {
                const serializedDuration = serializeStrategyDuration(duration);
                if (!tokenMap[mint][serializedDuration]) {
                    tokenMap[mint][serializedDuration] = { apyAmountUi: undefined };
                }
            });
        }
    }

    return tokenMap;
}

export function addMetadataBasedOnCollateral(
    collateralMintToInfo: Record<string, CollateralAmountsForm>,
    getMetadata: ReturnType<typeof useTokenListMetadataByMints>["getMetadata"]
): CollateralFormTableInfo[] {
    return Object.entries(collateralMintToInfo).map(([mint, value], index) => ({
        ...value,
        id: index,
        metadata: getMetadata(mint)
    }));
}

export function addMetadataBasedOnRateMints(
    rateToInfo: Record<string, Record<string, RateAmountForm>>,
    getMetadata: ReturnType<typeof useTokenListMetadataByMints>["getMetadata"]
): RateFormTableInfo[] {
    return Object.entries(rateToInfo).map(([mint, value], index) => ({
        ...value,
        id: index,
        metadata: getMetadata(mint)
    }));
}

// Fetches all strategy durations and populates the apy with presets for a given collateral.
// Returns a record of strategy duration to apy.
export function useDefaultDurationsAndApys(): { data: Record<string, string>; isLoading: boolean } {
    const { strategyDurations, isLoading } = useStrategyDurations();

    const durationsAndApys = useMemo(() => {
        const result: Record<string, string> = {};
        if (!strategyDurations) return result;
        strategyDurations.forEach((duration) => {
            result[serializeStrategyDuration(duration)] = DISABLED_APY; // Set default value to 0
        });
        return result;
    }, [strategyDurations]);

    return { data: durationsAndApys, isLoading };
}
